// ブランドネーム/その他ネーム/その他副資材の縦持ち表示をまとめる service です。
export function createFixedVerticalDisplayService(deps) {
    const {
        PANEL_TYPE,
        columninfo,
        selectedFukushizaiContextRef,
        isEmpty,
        DicVal,
        canonicalPanelType,
        normalizeSearchParamObject,
        normalizeFukushizaiContext,
        normalizeItemCd,
        normalizeGroupNo,
        normalizeColorCd,
        resolveFukushizaiDisplayMasterValue,
        setFukushizaiDisplayMeta,
        isKomonofudaPanelInfo
    } = deps;

    const FIXED_VERTICAL_DISPLAY_CONFIGS = {
        [PANEL_TYPE.BRAND_NAME]: {
            panelType: PANEL_TYPE.BRAND_NAME,
            label: "ブランドネーム",
            visibleCodeId: "BRANDNAME_CD_DMY",
            sourceColumnIds: ["SSDZ_BRANDNAMECD1", "SSDZ_BRANDNAMECD2", "SSDZ_BRANDNAMECD3"],
            rowLimit: 3,
            strictSourceType: PANEL_TYPE.BRAND_NAME,
            hideSourceColumns: false,
            removeColumnIds: ["BRANDNAME_NM_DMY"],
            baseRowMetaKey: "__brandNameBaseRowId",
            sourceColumnMetaKey: "__brandNameSourceColumnId"
        },
        [PANEL_TYPE.OTHER_NAME]: {
            panelType: PANEL_TYPE.OTHER_NAME,
            label: "その他ネーム",
            visibleCodeId: "SONOTANAME_CD_DMY",
            sourceColumnIds: [
                "SSDZ_SONOTANAMECD1",
                "SSDZ_SONOTANAMECD2",
                "SSDZ_SONOTANAMECD3",
                "SSDZ_SONOTANAMECD4",
                "SSDZ_SONOTANAMECD5",
                "SSDZ_SONOTANAMECD6"
            ],
            rowLimit: 6,
            strictSourceType: "",
            hideSourceColumns: true,
            removeColumnIds: [],
            baseRowMetaKey: "__otherNameBaseRowId",
            sourceColumnMetaKey: "__otherNameSourceColumnId"
        },
        [PANEL_TYPE.OTHER_FUKUZAI]: {
            panelType: PANEL_TYPE.OTHER_FUKUZAI,
            label: "その他副資材",
            visibleCodeId: "SONOTAFUZOKU_CD_DMY",
            sourceColumnIds: [
                "SSDZ_SONOTAFUZOKUCD1",
                "SSDZ_SONOTAFUZOKUCD2",
                "SSDZ_SONOTAFUZOKUCD3",
                "SSDZ_SONOTAFUZOKUCD4",
                "SSDZ_SONOTAFUZOKUCD5"
            ],
            rowLimit: 5,
            strictSourceType: "",
            hideSourceColumns: true,
            removeColumnIds: [],
            baseRowMetaKey: "__otherFukuzaiBaseRowId",
            sourceColumnMetaKey: "__otherFukuzaiSourceColumnId"
        }
    };

    // 候補配列から最初の非空文字列を返します。
    function pickDisplayText(values) {
        const list = Array.isArray(values) ? values : [values];
        for (let i = 0; i < list.length; i++) {
            const text = String(list[i] ?? "").trim();
            if (text !== "") return text;
        }
        return "";
    }

    // panel type から縦持ち表示設定を返します。
    function resolveFixedVerticalDisplayConfig(panelType) {
        const type = String(panelType ?? "").trim();
        return FIXED_VERTICAL_DISPLAY_CONFIGS[type] || null;
    }

    // panelInfo から縦持ち表示設定を返します。
    function resolveFixedVerticalDisplayConfigByPanelInfo(panelInfo) {
        return resolveFixedVerticalDisplayConfig(canonicalPanelType(DicVal(panelInfo, "type", "")));
    }

    // 元列定義の情報量を比較するためのスコアを算出します。
    function scoreSourceColInfo(item) {
        const scoreKeys = ["datatype", "dataid", "dataidctrl", "mastertype", "dbcolumn", "dbcolumnnm", "searchparam", "history"];
        let score = 0;
        scoreKeys.forEach((key) => {
            const value = key === "searchparam"
                ? normalizeSearchParamObject(DicVal(item, key, null))
                : DicVal(item, key, "");
            if (!isEmpty(value)) score += 10;
        });
        return score;
    }

    // 投影元に使う列定義を取得します。
    function findFixedVerticalSourceColInfo(config, items = columninfo) {
        if (isEmpty(config) || !Array.isArray(items) || items.length === 0) return null;
        const candidates = config.sourceColumnIds
            .map((sourceId) => {
                const upperSourceId = String(sourceId ?? "").trim().toUpperCase();
                const strict = config.strictSourceType === ""
                    ? null
                    : items.find((c) =>
                        String(DicVal(c, "columnid", "")).trim().toUpperCase() === upperSourceId
                        && canonicalPanelType(DicVal(c, "type", "")) === config.strictSourceType
                    );
                return strict
                    || items.find((c) => String(DicVal(c, "columnid", "")).trim().toUpperCase() === upperSourceId)
                    || null;
            })
            .filter((item) => !isEmpty(item))
            .map((item) => ({ item, score: scoreSourceColInfo(item) }))
            .sort((a, b) => b.score - a.score);
        return candidates.length > 0 ? candidates[0].item : null;
    }

    // 仮想表示列へ元列定義の属性を上書きします。
    function mergeFixedVerticalUiColInfo(item, sourceInfo, fallbackName) {
        if (isEmpty(item) || typeof item !== "object") return item;
        const next = { ...item };
        const source = isEmpty(sourceInfo) ? null : sourceInfo;
        const overlayKeys = [
            "datatype", "decimalplace", "options",
            "locked", "primary", "primarykey", "unchange", "required", "saveed", "multiple",
            "length", "format", "dataid", "dbcolumn", "dbcolumnnm", "keyinfo",
            "searchparam", "history", "copyclear", "extrigger", "extriggerref", "trigger",
            "mastertype", "selectlist"
        ];
        if (!isEmpty(source)) {
            overlayKeys.forEach((key) => {
                if (key in source) next[key] = source[key];
            });
        }
        next.columnnm = String(isEmpty(next.columnnm) ? (fallbackName || DicVal(source, "columnnm", "")) : next.columnnm);
        next.dataid = pickDisplayText([next.dataid, DicVal(source, "dataid", ""), DicVal(source, "dataidctrl", ""), DicVal(source, "mastertype", "")]);
        next.dataidctrl = pickDisplayText([next.dataidctrl, DicVal(source, "dataidctrl", ""), next.dataid]);
        next.mastertype = pickDisplayText([next.mastertype, DicVal(source, "mastertype", ""), next.dataid]);
        next.dbcolumn = pickDisplayText([next.dbcolumn, DicVal(source, "dbcolumn", ""), DicVal(source, "dbcolumnnm", "")]);
        next.dbcolumnnm = pickDisplayText([next.dbcolumnnm, DicVal(source, "dbcolumnnm", ""), next.dbcolumn]);
        next.searchparam = normalizeSearchParamObject(isEmpty(next.searchparam) ? DicVal(source, "searchparam", null) : next.searchparam);
        next.trigger = Array.isArray(next.trigger) ? next.trigger : [];
        next.history = isEmpty(next.history) ? true : next.history;
        return next;
    }

    // 縦持ち表示の再構築前に UI 専用列を取り除きます。
    function stripFixedVerticalDisplayFields(row, config) {
        if (isEmpty(row) || typeof row !== "object" || isEmpty(config)) return row;
        delete row[config.visibleCodeId];
        delete row[`${config.visibleCodeId}_cd`];
        delete row[`${config.visibleCodeId}_nm`];
        delete row[`${config.visibleCodeId}_CD`];
        delete row[`${config.visibleCodeId}_NM`];
        delete row[config.baseRowMetaKey];
        delete row[config.sourceColumnMetaKey];
        (Array.isArray(config.removeColumnIds) ? config.removeColumnIds : []).forEach((columnId) => {
            delete row[columnId];
        });
        return row;
    }

    // 元列を UI 上は hidden 扱いへ寄せます。
    function hideFixedVerticalSourceColumn(item) {
        const next = { ...(item || {}) };
        next.hide = true;
        next.initialHide = true;
        next.suppressColumnsToolPanel = true;
        return next;
    }

    // colinfo / columns に仮想表示列を追加します。
    function buildFixedVerticalDisplayColInfo(items, panelType) {
        if (!Array.isArray(items)) return items;
        const config = resolveFixedVerticalDisplayConfig(panelType);
        if (isEmpty(config)) return items;
        const sourceInfo = findFixedVerticalSourceColInfo(config, items);
        const sourceColumnIdSet = new Set(config.sourceColumnIds.map((id) => String(id ?? "").trim().toUpperCase()));
        const removeColumnIdSet = new Set((config.removeColumnIds || []).map((id) => String(id ?? "").trim().toUpperCase()));
        const visibleCodeId = String(config.visibleCodeId ?? "").trim().toUpperCase();
        const codeExists = items.some((c) => String(DicVal(c, "columnid", "")).trim().toUpperCase() === visibleCodeId);

        const out = items.map((item) => {
            if (isEmpty(item) || typeof item !== "object") return item;
            const cid = String(DicVal(item, "columnid", "")).trim().toUpperCase();
            if (sourceColumnIdSet.has(cid)) {
                return config.hideSourceColumns ? hideFixedVerticalSourceColumn(item) : item;
            }
            if (removeColumnIdSet.has(cid)) return null;
            if (cid === visibleCodeId) {
                const next = mergeFixedVerticalUiColInfo(item, sourceInfo, config.label);
                next.columnnm = config.label;
                next.table = "-";
                next.locked = false;
                next.saveed = false;
                next.history = true;
                return next;
            }
            return item;
        });

        if (!codeExists) {
            const next = mergeFixedVerticalUiColInfo({
                type: config.panelType,
                logpid: String(DicVal(sourceInfo, "logpid", "") ?? ""),
                table: "-",
                columnid: config.visibleCodeId,
                columnnm: config.label,
                datatype: "0",
                locked: false,
                saveed: false
            }, sourceInfo, config.label);
            next.columnnm = config.label;
            next.table = "-";
            next.locked = false;
            next.saveed = false;
            next.history = true;
            out.push(next);
        }

        return out.filter((item) => !isEmpty(item));
    }

    // 固定縦持ち panel の表示上限を返します。
    function fixedVerticalDisplayRowLimit(panelInfo) {
        if (isKomonofudaPanelInfo(panelInfo)) return 6;
        const config = resolveFixedVerticalDisplayConfigByPanelInfo(panelInfo);
        return Number(DicVal(config, "rowLimit", 0)) || 0;
    }

    // 固定縦持ち panel かどうかを返します。
    function isFixedVerticalDisplayPanelInfo(panelInfo) {
        return fixedVerticalDisplayRowLimit(panelInfo) > 0;
    }

    // 表示上限を超えた分は切り詰めます。
    function normalizeFixedVerticalDisplayRows(panelInfo, rows) {
        const limit = fixedVerticalDisplayRowLimit(panelInfo);
        const list = Array.isArray(rows) ? rows : [];
        if (limit <= 0) return list;
        return list.slice(0, limit);
    }

    // 表示行の元となる base row id を安定化します。
    function resolveFixedVerticalDisplayBaseRowId(panelType, sourceRow, fallbackContext = selectedFukushizaiContextRef.current) {
        const rowId = String(DicVal(sourceRow, "rowid", "") ?? "").trim();
        if (rowId !== "") return rowId;
        const context = normalizeFukushizaiContext(fallbackContext);
        const itemCd = normalizeItemCd(DicVal(sourceRow, "SSDZ_ITMCITEMCD", context.itemCd));
        const groupNo = normalizeGroupNo(DicVal(sourceRow, "SSDZ_FUKUSIZAICOLORGRP", context.groupNo));
        const colorCd = normalizeColorCd(DicVal(sourceRow, "SSDZ_COLORCD", context.colorCd));
        return [panelType, itemCd, groupNo, colorCd].filter((value) => value !== "").join("|");
    }

    // 固定縦持ち panel 用の表示行を生成します。
    function buildFixedVerticalDisplayRows(panelType, rows, contextOverride = null) {
        const config = resolveFixedVerticalDisplayConfig(panelType);
        if (isEmpty(config)) return Array.isArray(rows) ? rows : [];
        const list = Array.isArray(rows) ? rows : [];
        const buildContext = normalizeFukushizaiContext(contextOverride || selectedFukushizaiContextRef.current);
        const sourceRow = list.find((row) => !isEmpty(row)) || null;
        const baseRowId = resolveFixedVerticalDisplayBaseRowId(config.panelType, sourceRow, buildContext);
        const outRows = [];

        for (let index = 0; index < config.sourceColumnIds.length; index++) {
            const sourceColumnId = config.sourceColumnIds[index] || "";
            const nextRow = stripFixedVerticalDisplayFields({ ...(sourceRow || {}) }, config);
            nextRow.rowid = String(index);
            nextRow[config.baseRowMetaKey] = baseRowId;
            nextRow[config.sourceColumnMetaKey] = sourceColumnId;
            setFukushizaiDisplayMeta(nextRow, baseRowId, sourceColumnId);

            const resolved = resolveFukushizaiDisplayMasterValue({
                panelType: config.panelType,
                rowIndex: index,
                sourceColumnId,
                row: sourceRow,
                timing: "display-build",
                context: buildContext
            });

            nextRow[sourceColumnId] = resolved.code;
            nextRow[config.visibleCodeId] = resolved.displayValue;
            outRows.push(nextRow);
        }

        return outRows;
    }

    // ブランドネームの表示列定義を生成します。
    function buildBrandNameDisplayColInfo(items) {
        return buildFixedVerticalDisplayColInfo(items, PANEL_TYPE.BRAND_NAME);
    }

    // その他ネームの表示列定義を生成します。
    function buildOtherNameDisplayColInfo(items) {
        return buildFixedVerticalDisplayColInfo(items, PANEL_TYPE.OTHER_NAME);
    }

    // その他副資材の表示列定義を生成します。
    function buildOtherFukuzaiDisplayColInfo(items) {
        return buildFixedVerticalDisplayColInfo(items, PANEL_TYPE.OTHER_FUKUZAI);
    }

    // 現状ではブランドネーム列定義の追加加工は不要です。
    function buildBrandNameGridColumns(colDef) {
        return colDef;
    }

    // ブランドネームの表示行を生成します。
    function buildBrandNameDisplayRows(rows, contextOverride = null) {
        return buildFixedVerticalDisplayRows(PANEL_TYPE.BRAND_NAME, rows, contextOverride);
    }

    // その他ネームの表示行を生成します。
    function buildOtherNameDisplayRows(rows, contextOverride = null) {
        return buildFixedVerticalDisplayRows(PANEL_TYPE.OTHER_NAME, rows, contextOverride);
    }

    // その他副資材の表示行を生成します。
    function buildOtherFukuzaiDisplayRows(rows, contextOverride = null) {
        return buildFixedVerticalDisplayRows(PANEL_TYPE.OTHER_FUKUZAI, rows, contextOverride);
    }

    return {
        buildBrandNameDisplayColInfo,
        buildOtherNameDisplayColInfo,
        buildOtherFukuzaiDisplayColInfo,
        buildBrandNameGridColumns,
        isFixedVerticalDisplayPanelInfo,
        normalizeFixedVerticalDisplayRows,
        buildBrandNameDisplayRows,
        buildOtherNameDisplayRows,
        buildOtherFukuzaiDisplayRows
    };
}
